import { useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { animated, config, useSpring } from "@react-spring/web";
import {
  Card,
  Icon,
  Input,
  Layout,
  Modal,
  Popover,
  scrollListStyle,
  Title,
  useTheme
} from "@introist/react-foundation/v2";
import { ArrowLeftRight, Check, ChevronLeft, LogOut, Plus } from "lucide-react";

import { useMenuCollapsed } from "./context";
import { WorkspaceSelectorProps } from "./types";

// One selector for both places it appears: "full" (logo + name, in the side menu) and "logo"
// (logo only, at the bottom of the product rail). The dropdown is the same in both:
//   main view:   who you are · the current workspace (+ "Switch") · account links · sign out
//   switch view: the other workspaces, with search when there are many
// It only offers switching when you belong to more than one workspace.

const StyledCard = styled(Card)`
  width: 18rem;
  max-height: 36rem;
  padding: var(--spacing-large);
  padding-top: var(--spacing-medium);
  ${scrollListStyle};

  * {
    user-select: none;
  }
`;

// A full-width, left-aligned row: icon + label.
const MenuRow = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
  width: 100%;
  padding: var(--spacing-small);
  border: none;
  border-radius: var(--rounding-medium);
  background: transparent;
  color: ${({ $primary }) =>
    $primary ? "var(--palette-primary-default)" : "var(--palette-foreground-default)"};
  font-family: var(--typography-font-text);
  font-size: var(--typography-size-title);
  text-align: left;
  cursor: pointer;
  transition: background-color 160ms ease;

  > svg {
    flex-shrink: 0;
  }

  :hover {
    background-color: var(--palette-surface-default);
  }

  :focus-visible {
    outline: none;
    background-color: var(--palette-surface-default);
    box-shadow: inset 0 0 0 2px var(--palette-primary-default);
  }
`;

const LogoWrapper = styled.div<{ $size?: string }>`
  flex-shrink: 0;
  width: ${({ $size }) => $size ?? "1.5rem"};
  height: ${({ $size }) => $size ?? "1.5rem"};
  border-radius: var(--rounding-medium);
  overflow: hidden;

  > img {
    width: 100%;
    object-fit: contain;
  }
`;

const FirstCharacter = styled(Title)`
  width: inherit;
  height: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background-color: var(--palette-primary-subdued);
`;

const FullAnchorButton = styled.button<{ $active?: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: 1.5rem auto;
  grid-column-gap: var(--spacing-small);
  width: 100%;
  padding: var(--spacing-small);
  border: 1px solid var(--palette-border-subdued);
  border-radius: var(--rounding-medium);
  background-color: transparent;
  transition: border-color 160ms ease;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    border-color: var(--palette-primary-default);
  }

  ${({ $active }) =>
    $active &&
    css`
      border-color: var(--palette-primary-default);
    `}
`;

const EllipsisWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: stretch;
`;

const EllipsisTitle = styled(Title)`
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AnimatedWrapper = styled(animated.div)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

// The rail's anchor: just the logo, with a ring on hover and while open (like the side menu's
// anchor gets a border).
const LogoAnchorButton = styled.button<{ $active?: boolean }>`
  display: block;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: var(--rounding-medium);
  cursor: pointer;
  transition: box-shadow 160ms ease;

  &:hover,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--palette-border-default);
  }

  ${({ $active }) =>
    $active &&
    css`
      && {
        box-shadow: 0 0 0 2px var(--palette-primary-default);
      }
    `}
`;

// --- Account header ---------------------------------------------------------------------------

const AccountHeader = styled.div`
  margin-top: 4px;
  min-width: 0;
`;

const Caption = styled(Title)`
  color: var(--palette-foreground-subdued);
`;

// --- Current workspace + switching -------------------------------------------------------------

const SectionLabel = styled(Caption)`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: var(--spacing-xSmall);
`;

const CurrentWorkspace = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: var(--spacing-small);
  align-items: center;
  padding: var(--spacing-small);
  border-radius: var(--rounding-medium);
  background-color: var(--palette-surface-subdued);
`;

const SwitchButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--palette-border-subdued);
  border-radius: var(--rounding-small);
  background: var(--palette-surface-default);
  color: var(--palette-foreground-default);
  font-family: var(--typography-font-text);
  font-size: 12px;
  cursor: pointer;
  transition: border-color 160ms ease;

  :hover,
  :focus-visible {
    outline: none;
    border-color: var(--palette-primary-default);
  }
`;

const SwitchHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

const BackButton = styled.button`
  display: inline-flex;
  padding: 0.25rem;
  border: none;
  border-radius: var(--rounding-small);
  background: transparent;
  color: var(--palette-foreground-default);
  cursor: pointer;

  :hover {
    background: var(--palette-surface-default);
  }

  :focus-visible {
    outline: none;
    background: var(--palette-surface-default);
    box-shadow: inset 0 0 0 2px var(--palette-primary-default);
  }
`;

const WorkspaceOption = styled.button<{ $current: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: var(--spacing-small);
  align-items: center;
  width: 100%;
  padding: var(--spacing-small);
  border: 1px solid transparent;
  border-radius: var(--rounding-medium);
  background-color: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 160ms ease;

  :hover {
    background-color: var(--palette-surface-default);
  }

  :focus-visible {
    outline: none;
    background-color: var(--palette-surface-default);
    box-shadow: inset 0 0 0 2px var(--palette-primary-default);
  }

  ${({ $current }) =>
    $current &&
    css`
      border-color: var(--palette-primary-default);
      cursor: default;
    `}
`;

// Above this many workspaces, the switch view gets a search box.
const SEARCH_THRESHOLD = 8;

const firstCharacter = (input: string) => (input.length > 0 ? input[0].toUpperCase() : null);

const Logo = ({ name, logo, size }: { name: string; logo?: React.ReactNode; size?: string }) => (
  <LogoWrapper $size={size}>
    {logo ? <>{logo}</> : <FirstCharacter variant="bold">{firstCharacter(name)}</FirstCharacter>}
  </LogoWrapper>
);

const FullAnchor = ({
  organisationName,
  logo,
  open,
  onClick,
  anchorRef
}: {
  organisationName: string;
  logo: React.ReactNode;
  open: boolean;
  onClick: () => void;
  anchorRef: React.MutableRefObject<HTMLElement | null>;
}) => {
  const { theme } = useTheme();
  const collapsed = useMenuCollapsed();

  const spring = useSpring({
    opacity: collapsed ? 0 : 1,
    transform: collapsed ? "translateX(0.5rem)" : "translateX(0rem)",
    config: { ...config.stiff, clamp: true }
  });

  return (
    <FullAnchorButton
      ref={el => (anchorRef.current = el)}
      onClick={onClick}
      $active={open}
      aria-expanded={open}
    >
      <Logo name={organisationName} logo={logo} />
      <AnimatedWrapper style={spring}>
        <EllipsisWrapper>
          <EllipsisTitle variant="bold" style={{ maxWidth: 106 }}>
            {organisationName}
          </EllipsisTitle>
        </EllipsisWrapper>
        <Icon
          name="caretDown"
          color={theme.palette.foreground.subdued}
          style={{ marginLeft: "auto" }}
        />
      </AnimatedWrapper>
    </FullAnchorButton>
  );
};

export const WorkspaceSelector = ({
  variant,
  user,
  workspaces,
  organisation,
  currentWorkspaceId,
  createWorkspace,
  switchWorkspace,
  onLogout,
  accountItems = []
}: WorkspaceSelectorProps & { variant: "full" | "logo" }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"main" | "switch">("main");
  const anchorRef = useRef<HTMLElement | null>(null);

  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [search, setSearch] = useState("");

  const close = () => {
    setOpen(false);
    setView("main");
    setSearch("");
  };
  const toggle = () => (open ? close() : setOpen(true));

  const isCurrent = (workspace: { id: string; name: string }) =>
    currentWorkspaceId ? workspace.id === currentWorkspaceId : workspace.name === organisation.name;

  const canSwitch = !!switchWorkspace && !!workspaces && workspaces.length > 1;
  const showSearch = !!workspaces && workspaces.length > SEARCH_THRESHOLD;
  const shownWorkspaces = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!workspaces || !query) return workspaces ?? [];
    return workspaces.filter(w => w.name.toLowerCase().includes(query));
  }, [workspaces, search]);

  const mainView = (
    <Layout.Group vertical gap="large">
      <AccountHeader title={user.email}>
        <Caption small>Signed in as</Caption>
        <EllipsisTitle variant="bold">{user.email}</EllipsisTitle>
      </AccountHeader>

      <div>
        <SectionLabel small>Workspace</SectionLabel>
        <CurrentWorkspace>
          <Logo name={organisation.name} logo={organisation.logo} />
          <EllipsisTitle variant="bold">{organisation.name}</EllipsisTitle>
          {canSwitch && (
            <SwitchButton onClick={() => setView("switch")}>
              <ArrowLeftRight size={12} />
              Switch
            </SwitchButton>
          )}
        </CurrentWorkspace>
        {createWorkspace && (
          <MenuRow
            $primary
            style={{ marginTop: "var(--spacing-small)" }}
            onClick={() => {
              close();
              setCreateWorkspaceOpen(true);
            }}
          >
            <Plus size={16} />
            Create workspace
          </MenuRow>
        )}
      </div>

      <Layout.Divider />
      <Layout.Group vertical gap="xSmall">
        {accountItems.map(item => (
          <MenuRow
            key={item.key}
            onClick={() => {
              close();
              item.onClick();
            }}
          >
            {item.icon}
            {item.title}
          </MenuRow>
        ))}
        <MenuRow
          onClick={() => {
            close();
            onLogout();
          }}
        >
          <LogOut size={16} />
          Sign out
        </MenuRow>
      </Layout.Group>
    </Layout.Group>
  );

  const switchView = (
    <Layout.Group vertical gap="medium">
      <SwitchHeader>
        <BackButton aria-label="Back" onClick={() => setView("main")}>
          <ChevronLeft size={16} />
        </BackButton>
        <Title variant="bold">Switch workspace</Title>
      </SwitchHeader>

      {showSearch && view === "switch" && (
        <Input
          autoFocus
          size="small"
          placeholder="Search workspaces"
          value={search}
          onChange={setSearch}
        />
      )}

      <Layout.Group vertical gap="xSmall">
        {shownWorkspaces.length === 0 && <Caption small>No workspaces match</Caption>}
        {shownWorkspaces.map(workspace => {
          const current = isCurrent(workspace);
          return (
            <WorkspaceOption
              key={workspace.id}
              $current={current}
              aria-current={current || undefined}
              onClick={() => {
                if (current) return;
                close();
                switchWorkspace && switchWorkspace(workspace.id);
              }}
            >
              <LogoWrapper>
                {workspace.logo ? (
                  <img src={workspace.logo} alt="" />
                ) : (
                  <FirstCharacter variant="bold">{firstCharacter(workspace.name)}</FirstCharacter>
                )}
              </LogoWrapper>
              <EllipsisTitle variant="bold">{workspace.name}</EllipsisTitle>
              {current ? <Check size={16} color="var(--palette-primary-default)" /> : <span />}
            </WorkspaceOption>
          );
        })}
      </Layout.Group>
    </Layout.Group>
  );

  return (
    <div>
      {variant === "full" ? (
        <FullAnchor
          organisationName={organisation.name}
          logo={organisation.logo}
          open={open}
          onClick={toggle}
          anchorRef={anchorRef}
        />
      ) : (
        <LogoAnchorButton
          ref={el => (anchorRef.current = el)}
          onClick={toggle}
          $active={open}
          aria-label={`${organisation.name}: workspace and account`}
          aria-expanded={open}
        >
          <Logo name={organisation.name} logo={organisation.logo} />
        </LogoAnchorButton>
      )}

      {/* Clicks inside don't close it (closeOnContentClick=false): switching views and searching
          happen in place, and every action closes it explicitly. */}
      <Popover
        open={open}
        referenceElement={anchorRef.current}
        onClose={close}
        closeOnContentClick={false}
      >
        <StyledCard elevated>
          {/* Both views stay mounted and the inactive one is hidden: the popover closes on a
              document click whose target isn't inside it, and swapping the views would detach
              the clicked "Switch"/"Back" button before that check runs. */}
          <div hidden={view !== "main" && canSwitch}>{mainView}</div>
          {canSwitch && <div hidden={view !== "switch"}>{switchView}</div>}
        </StyledCard>
      </Popover>

      {createWorkspace && (
        <Modal
          title="Create new workspace"
          open={createWorkspaceOpen}
          onClose={() => setCreateWorkspaceOpen(false)}
          style={{ width: "350px" }}
          primaryButton={{
            title: "Create",
            onClick: () => createWorkspace(newWorkspaceName)
          }}
          secondaryButton={{
            title: "Cancel",
            onClick: () => setCreateWorkspaceOpen(false)
          }}
        >
          <Input
            autoFocus
            placeholder="Workspace name"
            value={newWorkspaceName}
            onChange={setNewWorkspaceName}
          />
        </Modal>
      )}
    </div>
  );
};
